import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import {
    Monitor,
    ShieldCheck,
    Headset,
    Clock,
    RefreshCw,
    Network,
    Phone,
    Mail,
    MapPin,
    Menu,
    X,
    ExternalLink,
    Instagram,
    MessageCircle,
    Users
} from 'lucide-react';
import { FaWhatsapp, FaInstagram } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import VideoGallery from './VideoGallery';
import FeedbackSection from './FeedbackSection';

const LandingPage = ({ onLoginClick }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const logo = require('../lib/logo.png');
    const aplizeLogo = require('../lib/aplize_logo.png');

    const heroBg = require('../lib/hero_page.png');

    // Handle scroll effect for navbar
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setMobileMenuOpen(false);
        }
    };

    return (
        <div className="min-h-screen text-white selection:bg-[#FF7D00] selection:text-white overflow-x-hidden font-sans relative">

            {/* Uniform Background Image with Overlay */}
            <div className="fixed inset-0 z-0">
                <img
                    src={heroBg}
                    alt="Background"
                    className="absolute inset-0 w-full h-full object-cover brightness-125"
                />
                <div className="absolute inset-0 bg-[#001524]/60 mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#001524] via-transparent to-[#001524]/40" />
            </div>

            {/* Decorative Background Elements (Blobs) - Retained for "Beautification" */}
            <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0 opacity-60">
                <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-sky-500/10 rounded-full blur-[120px]" />
                <div className="absolute top-[20%] right-[-10%] w-[40%] h-[40%] bg-orange-500/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-20%] left-[20%] w-[50%] h-[50%] bg-sky-500/10 rounded-full blur-[120px]" />
            </div>

            {/* Navbar */}
            <nav
                className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#001524]/80 backdrop-blur-md border-b border-white/5 py-4"
            >
                <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-white/10 p-2 rounded-xl border border-white/10 backdrop-blur-sm">
                            <img src={logo} alt="JIT Logo" className="w-8 h-8 object-contain animate-spin-slow" />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-2xl font-bold tracking-tight bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent hidden sm:block">
                                Jay Info Tech
                            </span>
                            <span className="text-[0.65rem] text-white font-medium tracking-widest uppercase hidden sm:block">
                                Since 2018
                            </span>
                        </div>
                    </div>

                    {/* Desktop Nav - Premium Glassmorphism */}
                    <div className="hidden md:flex items-center gap-3">
                        {['About', 'Services', 'Feedback', 'Why Us', 'Contact Us'].map((item) => (
                            <button
                                key={item}
                                onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                                className="relative px-5 py-2.5 rounded-full text-sm font-semibold text-gray-300 transition-all duration-300 overflow-hidden group hover:text-white"
                            >
                                {/* Hover Spotlight Effect */}
                                <span className="absolute inset-0 w-full h-full bg-gradient-to-tr from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></span>

                                {/* Bottom Glow Line */}
                                <span className="absolute bottom-0 left-1/2 w-0 h-[2px] bg-sky-400 group-hover:w-1/2 group-hover:-translate-x-1/2 transition-all duration-500 ease-out shadow-[0_0_8px_rgba(56,189,248,0.8)]"></span>

                                <span className="relative z-10 tracking-wide">{item}</span>
                            </button>
                        ))}

                        <Button
                            onClick={onLoginClick}
                            className="ml-4 relative group overflow-hidden bg-gradient-to-r from-cyan-500 via-blue-600 to-cyan-500 bg-[length:200%_auto] hover:bg-[position:right_center] transition-all duration-500 text-white rounded-full px-8 py-6 font-bold shadow-[0_0_20px_rgba(6,182,212,0.5)] hover:shadow-[0_0_40px_rgba(6,182,212,0.7)] border border-cyan-400/30 ring-2 ring-cyan-500/20 hover:ring-cyan-400/50"
                        >
                            {/* Constant Shine Animation */}
                            <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent z-10"></div>
                            <div className="absolute top-1 right-2 w-1 h-1 bg-white rounded-full opacity-0 group-hover:animate-ping transition-all"></div>
                            <div className="absolute bottom-2 left-3 w-0.5 h-0.5 bg-white rounded-full opacity-0 group-hover:animate-pulse delay-100 transition-all"></div>

                            <span className="relative z-10 flex items-center gap-2.5">
                                Login
                                <div className="bg-white/20 p-1.5 rounded-full group-hover:rotate-45 transition-transform duration-500 backdrop-blur-sm border border-white/30">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                                </div>
                            </span>
                        </Button>
                    </div>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="md:hidden text-white"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        {mobileMenuOpen ? <X /> : <Menu />}
                    </button>
                </div>

                {/* Mobile Menu - Ultra Premium Glassmorphism */}
                {mobileMenuOpen && (
                    <div className="md:hidden absolute top-full left-0 w-full max-h-[80vh] overflow-y-auto bg-gradient-to-b from-[#001524]/95 to-[#001524]/90 backdrop-blur-3xl border-b border-cyan-500/10 py-8 px-5 flex flex-col gap-5 shadow-[0_8px_32px_rgba(0,0,0,0.8)] animate-in slide-in-from-top-4 z-50">
                        {['About', 'Services', 'Feedback', 'Why Us', 'Contact Us'].map((item, index) => (
                            <button
                                key={item}
                                onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                                className="relative text-left w-full overflow-hidden group p-5 rounded-2xl border-2 border-cyan-500/20 bg-gradient-to-br from-cyan-500/10 via-blue-600/5 to-transparent hover:from-cyan-500/20 hover:via-blue-600/10 transition-all duration-300 shadow-[0_4px_16px_rgba(6,182,212,0.15)] hover:shadow-[0_8px_24px_rgba(6,182,212,0.3)] active:scale-[0.97]"
                                style={{ animationDelay: `${index * 50}ms` }}
                            >
                                {/* Card Glow Border */}
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-400/0 via-cyan-400/20 to-cyan-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                {/* Active Accent Line */}
                                <div className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-cyan-400 via-blue-500 to-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-l-2xl shadow-[0_0_12px_rgba(6,182,212,0.6)]"></div>

                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>

                                <div className="flex items-center justify-between relative z-10">
                                    <div className="flex items-center gap-3">
                                        {/* Icon Circle */}
                                        <div className="w-10 h-10 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center group-hover:bg-cyan-500/20 group-hover:border-cyan-400/50 transition-all duration-300">
                                            <div className="w-2 h-2 rounded-full bg-cyan-400 group-hover:animate-pulse"></div>
                                        </div>
                                        <span className="text-xl font-bold text-white/90 group-hover:text-white transition-colors drop-shadow-lg">{item}</span>
                                    </div>
                                    <svg className="w-6 h-6 text-cyan-400 opacity-50 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7"></path></svg>
                                </div>
                            </button>
                        ))}

                        {/* Login Button Section with Separator */}
                        <div className="mt-4 pt-4 border-t-2 border-cyan-500/30">
                            <Button
                                onClick={onLoginClick}
                                className="w-full relative group overflow-hidden bg-gradient-to-r from-cyan-500 via-blue-600 to-cyan-500 bg-[length:200%_auto] hover:bg-[position:right_center] transition-all duration-500 text-white rounded-2xl py-7 font-extrabold text-xl shadow-[0_6px_24px_rgba(6,182,212,0.5)] hover:shadow-[0_8px_32px_rgba(6,182,212,0.7)] active:scale-[0.98] border-2 border-cyan-400/40 ring-2 ring-cyan-400/30"
                            >
                                {/* Intense Shine Animation */}
                                <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent z-10"></div>

                                <span className="relative z-20 flex items-center justify-center gap-3 drop-shadow-lg">
                                    <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
                                    Client Login
                                    <div className="bg-white/30 p-1.5 rounded-full group-hover:rotate-45 transition-transform duration-500 border border-white/40">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                                    </div>
                                </span>
                            </Button>
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 container mx-auto px-4 md:px-6 z-10">
                <div className="max-w-4xl mx-auto text-center space-y-8">
                    <div className="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium tracking-wide animate-in fade-in slide-in-from-bottom-4 duration-700 backdrop-blur-sm hover:bg-emerald-500/20 hover:border-emerald-500/30 transition-all cursor-default shadow-[0_0_15px_rgba(16,185,129,0.1)]">
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 duration-1000"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gradient-to-r from-emerald-400 to-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></span>
                        </span>
                        Trusted IT Solutions Provider
                    </div>

                    <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white leading-[1.1] animate-in fade-in slide-in-from-bottom-6 duration-700 delay-100">
                        Complete IT <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-[#FF7D00]">Sales, Services</span> & <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF7D00] to-sky-400">Rental</span> Solutions
                    </h1>

                    <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
                        Reliable computer and laptop sales, rental, and support services for businesses, startups, and individuals. Your one-stop destination for all IT infrastructure needs.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-10 duration-700 delay-300">
                        <Button
                            onClick={() => scrollToSection('services')}
                            size="lg"
                            className="bg-white text-[#001524] hover:bg-gray-100 rounded-full px-8 text-base font-bold shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all hover:scale-105"
                        >
                            Explore Services
                        </Button>
                    </div>
                </div>
            </section>

            {/* Stats/Ticker - Premium Glassmorphism */}
            <div id="why-us" className="border-y border-white/10 bg-black/45 backdrop-blur-[8px] relative z-10 w-full">
                <div className="container mx-auto px-4 py-12">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {[
                            { label: "Satisfied Clients", value: "500+" },
                            { label: "Systems Rented", value: "1000+" },
                            { label: "Years Experience", value: `${new Date().getFullYear() - 2018}` },
                            { label: "Service Uptime", value: "90%" }
                        ].map((stat, i) => (
                            <div key={i} className="flex flex-col items-center justify-center group">
                                <div className="text-4xl md:text-5xl font-extrabold text-white mb-2 tracking-tight group-hover:text-sky-400 transition-colors duration-300 drop-shadow-md">
                                    {stat.value}
                                </div>
                                <div className="text-sm md:text-base text-gray-300 font-medium tracking-widest uppercase opacity-90">
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* About Section */}
            {/* About Section - Card-Based Modular UI (Enterprise SaaS Style) */}
            <section id="about" className="py-24 relative z-10">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                        {/* Main Content Card */}
                        <div className="bg-[#001524]/60 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-12 shadow-2xl relative overflow-hidden group h-full flex flex-col justify-center">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                            <div className="relative z-10">
                                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                                    Tailored Solutions for <span className="text-sky-400">Modern Businesses</span>
                                </h2>
                                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                                    Jay Info Tech specializes in delivering cost-effective and high-performance IT solutions tailored to offices, call centers, startups, events, and home users.
                                </p>
                                <p className="text-gray-400 leading-relaxed mb-8">
                                    We ensure quick setup, dependable hardware, and prompt service to keep your operations running smoothly. With a focus on quality, transparency, and customer satisfaction, we are your trusted technology partner.
                                </p>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {[
                                        "Call center Technical solutions",
                                        "Call center setup",
                                        "Dialer Solutions",
                                        "Call center Headset",
                                        "Call center headphones",
                                        "Call center solution in Solapur",
                                        "GSM Gateway solution",
                                        "SIM card based dialer",
                                        "Computer sales and service",
                                        "Rental computer and laptop"
                                    ].map((item, index) => (
                                        <div key={index} className="flex items-center gap-2.5 p-2 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-sky-500/30 transition-all duration-300 group/feature">
                                            <div className="p-1.5 rounded-lg bg-sky-500/10 text-sky-400 group-hover/feature:bg-sky-500/20 group-hover/feature:scale-105 transition-all">
                                                <ShieldCheck className="w-3.5 h-3.5" />
                                            </div>
                                            <span className="text-xs font-semibold text-gray-300 group-hover/feature:text-white transition-colors">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Feature Cards Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {[
                                { title: "Cost-effective solutions", icon: <ShieldCheck className="w-6 h-6 text-emerald-400" /> },
                                { title: "High-performance hardware", icon: <Monitor className="w-6 h-6 text-sky-400" /> },
                                { title: "Tailored for Offices & Startups", icon: <Users className="w-6 h-6 text-[#FF7D00]" /> },
                                { title: "Prompt Technical Support", icon: <Headset className="w-6 h-6 text-purple-400" /> }
                            ].map((feature, i) => (
                                <div key={i} className="bg-[#001524]/40 backdrop-blur-md border border-white/5 rounded-2xl p-6 hover:bg-white/5 hover:border-sky-500/30 transition-all duration-300 group">
                                    <div className="mb-4 p-3 bg-white/5 rounded-xl w-fit group-hover:scale-110 transition-transform duration-300 shadow-[0_0_15px_rgba(0,0,0,0.3)]">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-white font-semibold text-lg">{feature.title}</h3>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section id="services" className="py-20 md:py-32 bg-[#001e33]/50 relative z-10">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Our Services</h2>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            From rentals to repairs, we cover every aspect of your IT infrastructure lifecycle.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[{
                            icon: <Phone className="h-8 w-8 text-white" />,
                            bg: "bg-indigo-500",
                            title: "Complete Call Center Solutions",
                            desc: "From high-performance dialer servers and GSM gateways to crystal-clear headphone setups, we provide the technical backbone your team needs to succeed."
                        },
                        {
                            icon: <Clock className="h-8 w-8 text-white" />,
                            bg: "bg-[#FF7D00]",
                            title: "IT Rentals",
                            desc: "Short-term and long-term laptop and desktop rentals for events, projects, or workforce expansion."
                        },
                        {
                            icon: <Monitor className="h-8 w-8 text-white" />,
                            bg: "bg-sky-500",
                            title: "Sales & Procurement",
                            desc: "New and high-quality refurbished systems tailored to your performance needs and budget."
                        },
                        {
                            icon: <ShieldCheck className="h-8 w-8 text-white" />,
                            bg: "bg-emerald-500",
                            title: "AMC Services",
                            desc: "Annual Maintenance Contracts to ensure your hardware stays in peak condition year-round."
                        },
                        {
                            icon: <Network className="h-8 w-8 text-white" />,
                            bg: "bg-indigo-500",
                            title: "Networking Solutions",
                            desc: "Complete setup and maintenance of office networks for seamless connectivity."
                        },
                        {
                            icon: <Headset className="h-8 w-8 text-white" />,
                            bg: "bg-pink-500",
                            title: "Technical Support",
                            desc: "Expert on-site and remote assistance to resolve software and hardware issues instantly."
                        },
                        {
                            icon: <RefreshCw className="h-8 w-8 text-white" />,
                            bg: "bg-yellow-500",
                            title: "System Upgrades",
                            desc: "Hardware upgrades to breathe new life into your existing infrastructure."
                        },
                        {
                            icon: <Mail className="h-8 w-8 text-white" />,
                            bg: "bg-blue-600",
                            title: "Microsoft Outlook Solutions",
                            desc: "Microsoft Outlook is more than just email—it’s a complete productivity and service-management tool when integrated properly with Microsoft 365 and business systems."
                        },
                        {
                            icon: <Mail className="h-8 w-8 text-white" />,
                            bg: "bg-red-500",
                            title: "Gmail Solutions",
                            desc: "Google Workspace (formerly G Suite) provides professional email and collaboration solutions for service-based businesses, IT firms, and enterprises."
                        }
                        ].map((service, i) => (
                            <div key={i} className="group relative bg-[#001524]/60 backdrop-blur-xl border border-white/5 rounded-3xl p-8 hover:bg-[#001e33] transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-sky-900/20 overflow-hidden">
                                <div className={`absolute top-0 right-0 w-32 h-32 ${service.bg} opacity-10 rounded-bl-full group-hover:scale-150 transition-transform duration-500`}></div>

                                <div className={`w-16 h-16 rounded-2xl ${service.bg} bg-opacity-20 flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                    <div className={`${service.bg} p-3 rounded-xl shadow-inner`}>
                                        {service.icon}
                                    </div>
                                </div>

                                <h3 className="text-xl md:text-2xl font-bold text-white mb-4 group-hover:text-primary transition-colors">
                                    {service.title}
                                </h3>

                                <p className="text-white leading-relaxed opacity-90">
                                    {service.desc}
                                </p>

                                <div className="mt-6 flex items-center text-sm font-semibold text-sky-400 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                                    Learn more <ExternalLink className="ml-2 w-4 h-4" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>



            {/* Video Gallery Section */}
            <VideoGallery />

            {/* Banner CTA */}
            {/* Banner CTA */}
            <section className="py-20 relative z-10">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="relative rounded-3xl p-8 md:p-16 overflow-hidden shadow-2xl group border border-white/10">
                        {/* Background Gradients & Effects */}
                        <div className="absolute inset-0 bg-gradient-to-r from-sky-900/90 to-[#001524] backdrop-blur-xl transition-all duration-500"></div>
                        <div className="absolute top-0 right-0 w-96 h-96 bg-sky-500/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 group-hover:bg-sky-500/30 transition-colors duration-500"></div>
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF7D00]/10 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2"></div>

                        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
                            <div className="max-w-2xl">
                                <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                                    Ready to upgrade your <br />
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-[#FF7D00]">IT Infrastructure?</span>
                                </h2>
                                <p className="text-sky-100/90 text-lg md:text-xl leading-relaxed">
                                    Join hundreds of satisfied businesses who trust Jay Info Tech for their rentals, sales, and support needs.
                                </p>
                            </div>
                            <div className="flex-shrink-0">
                                <Button
                                    onClick={() => document.getElementById('contact-us').scrollIntoView({ behavior: 'smooth' })}
                                    className="bg-white text-[#001524] hover:bg-sky-50 px-8 py-6 rounded-xl text-lg font-bold shadow-lg shadow-sky-900/20 hover:shadow-sky-500/20 hover:-translate-y-1 transition-all duration-300"
                                >
                                    Contact Us
                                    <MessageCircle className="ml-2 w-5 h-5" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feedback Section */}
            <FeedbackSection />

            {/* Contact / Locate Us Section */}
            <section id="contact-us" className="py-20 md:py-32 relative z-10">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs font-black mb-8 backdrop-blur-md shadow-lg">
                            <div className="flex items-center justify-center p-1 bg-[#FF7D00] rounded-md shadow-[0_0_12px_rgba(255,125,0,0.4)]">
                                <MapPin className="h-3.5 w-3.5 text-white" />
                            </div>
                            <span className="tracking-[0.2em] uppercase opacity-90">Our Presence</span>
                        </div>
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Contact Us</h2>
                        <p className="text-xl text-white max-w-2xl mx-auto">
                            Visit our office for personalized IT solutions, system demos, and expert consultations.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
                        {/* Info Column */}
                        <div className="flex flex-col gap-6">
                            <Card className="bg-white/5 border-white/10 overflow-hidden backdrop-blur-sm flex-1">
                                <CardHeader className="pb-4">
                                    <CardTitle className="text-2xl text-white flex items-center gap-3">
                                        Office Information
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Corporate Address */}
                                    <div className="flex items-start gap-5 p-6 rounded-3xl bg-[#001524]/60 border border-white/5 hover:bg-[#001e33] transition-all duration-300 group shadow-lg hover:shadow-sky-900/20">
                                        <div className="p-4 rounded-xl bg-[#FF7D00]/20 text-[#FF7D00] shadow-[0_0_15px_rgba(255,125,0,0.3)] group-hover:scale-110 transition-transform duration-300">
                                            <MapPin className="h-6 w-6" />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-bold text-white mb-2">Corporate Address</h4>
                                            <p className="text-gray-300 leading-relaxed font-medium">
                                                <span className="text-white block mb-1">Branch 1 :</span> Bhawani Path, Madal Wasti, Solapur - 413002
                                            </p>
                                            <p className="text-gray-300 leading-relaxed font-medium mt-2">
                                                <span className="text-white block mb-1">Branch 2 :</span> Thobade Mala, Degaon Road, Behind Reliance Market - 413001.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Get in Touch */}
                                    <div className="flex items-start gap-5 p-6 rounded-3xl bg-[#001524]/60 border border-white/5 hover:bg-[#001e33] transition-all duration-300 group shadow-lg hover:shadow-sky-900/20">
                                        <div className="p-4 rounded-xl bg-sky-500/20 text-sky-400 shadow-[0_0_15px_rgba(14,165,233,0.3)] group-hover:scale-110 transition-transform duration-300">
                                            <Phone className="h-6 w-6" />
                                        </div>
                                        <div className="w-full">
                                            <h4 className="text-xl font-bold text-white mb-2">Get in Touch</h4>
                                            <p className="text-white text-lg font-semibold tracking-wide mb-4">+91 8669178491</p>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* WhatsApp */}
                                                <a
                                                    href="https://wa.me/918669178491"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-3 p-3 rounded-xl bg-[#25D366]/10 text-white hover:bg-[#25D366]/20 border border-[#25D366]/20 transition-all group/wa"
                                                >
                                                    <FaWhatsapp size={20} className="text-[#25D366] group-hover/wa:scale-110 transition-transform" />
                                                    <span className="font-semibold text-sm">WhatsApp</span>
                                                </a>

                                                {/* Instagram */}
                                                <a
                                                    href="https://www.instagram.com/jit_computer_laptop/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-3 p-3 rounded-xl bg-[#E4405F]/10 text-white hover:bg-[#E4405F]/20 border border-[#E4405F]/20 transition-all group/ig"
                                                >
                                                    <FaInstagram size={20} className="text-[#E4405F] group-hover/ig:scale-110 transition-transform" />
                                                    <span className="font-semibold text-sm">Instagram</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Email Support */}
                                    <div className="flex items-start gap-5 p-6 rounded-3xl bg-[#001524]/60 border border-white/5 hover:bg-[#001e33] transition-all duration-300 group shadow-lg hover:shadow-sky-900/20">
                                        <div className="p-4 rounded-xl bg-green-500/20 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.3)] group-hover:scale-110 transition-transform duration-300">
                                            <HiOutlineMail size={24} />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-bold text-white mb-2">Email Support</h4>
                                            <a
                                                href="https://mail.google.com/mail/?view=cm&fs=1&to=jayinfotech20@gmail.com&su=Support+Request"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-white hover:text-sky-400 transition-colors font-medium break-all"
                                            >
                                                jayinfotech20@gmail.com
                                            </a>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Button
                                asChild
                                size="lg"
                                className="w-full bg-[#FF7D00] hover:bg-[#FF8D20] text-white h-16 rounded-2xl font-bold text-lg shadow-xl shadow-[#FF7D00]/10 hover:shadow-[#FF7D00]/20 transition-all gap-3 border-t border-white/10"
                            >
                                <a href="https://maps.app.goo.gl/TDTAgpCT62LgY5JM8" target="_blank" rel="noopener noreferrer">
                                    Open in Google Maps <ExternalLink className="h-5 w-5" />
                                </a>
                            </Button>
                        </div>

                        {/* Map Column */}
                        <div className="relative group rounded-3xl overflow-hidden border border-white/10 shadow-2xl min-h-[400px] lg:min-h-full">
                            <div className="absolute inset-0 bg-gradient-to-tr from-[#FF7D00]/20 to-sky-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-10"></div>

                            <iframe
                                title="JIT Location"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4509.537188624631!2d75.8852045!3d17.665616200000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc5d1a3ed04eb51%3A0x5e5e001cb6e2812e!2sJay%20Info%20Tech!5e1!3m2!1sen!2sin!4v1767460314992!5m2!1sen!2sin"
                                width="100%"
                                height="100%"
                                style={{
                                    border: 0,
                                    filter: 'invert(90%) hue-rotate(180deg) brightness(95%) contrast(90%) opacity(0.8)'
                                }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                className="absolute inset-0 w-full h-full object-cover"
                            ></iframe>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#000a12] border-t border-white/5 py-6 relative z-10">
                <div className="container mx-auto px-4 md:px-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-2.5">
                        <div className="bg-white/10 p-1.5 rounded-lg">
                            <img src={logo} alt="JIT Logo" className="h-10 w-auto object-contain animate-spin-slow" />
                        </div>
                        <span className="text-lg font-bold text-white">Jay Info Tech</span>
                    </div>
                    <div className="text-gray-500 text-xs">
                        &copy; {new Date().getFullYear()} Jay Info Tech Solutions. All rights reserved.
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                        <span className="text-gray-600 text-[10px] font-semibold uppercase tracking-widest">Powered by APlize Digital</span>
                        <div className="bg-white/95 p-2 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105">
                            <a href="https://aplize98.github.io/"><img src={aplizeLogo} alt="APlize Digital" className="h-8 w-auto object-contain" /></a>
                        </div>
                    </div>
                </div>
            </footer>
        </div >
    );
};

export default LandingPage;
